import { AnyAction } from "redux";
import { amrPipelineActions as actionTypes, constants } from "../constants";
import { AmrDealsState, KMatrixViewType } from '../types/state/AmrDealsState';
import { PipelineType } from '../types/amr-pipeline/enums/PipelineType';
import { sortSyndicateContacts, withIndicatingRefinancedIoIs } from '../utils/amr-pipeline.utils';
import { OriginatingTransaction } from "../types/amr-pipeline/models/OriginatingTransaction";
import { RequestState } from "../constants/request-state";

const initialState: AmrDealsState = {
    isTransactionsSearching: false,
    summaryRequestState: RequestState.none,
    isClassesSearching: false,
    isManagerPopupVisible: false,
    kMatrixOffset: 0,
    isKMatrixSearching: false,
    hasMoreKMatrix: true,
    kMatrixViewType: KMatrixViewType.Default,
    kMatrixViewTypeAutoChange: undefined,
    transactionsOffset: 0,
    classesOffset: 0,
    isExpanded: false,
    hasMoreTransactions: true,
    hasMoreTransactionsClasses: true,
    wasInitialAccessTracked: false,
    isExportLoad: false,
    syndicateContacts: null,
    totalKMatrixClassItemsCount: 0,
    anyIOIsExists: true,
};

export const createAmrDealsWithNamedData = (pipelineType: PipelineType) => (
    state: AmrDealsState = initialState,
    action: AnyAction
): AmrDealsState => {
    if (action.pipelineType !== pipelineType) {
        return state;
    }

    switch (action.type) {
        case actionTypes.PIPELINE_INITIAL_LOAD:
            return {
                ...state,
                wasInitialAccessTracked: true,
            };
        case actionTypes.PIPELINE_SEARCH_TRANSACTIONS:
            return {
                ...state,
                isTransactionsSearching: action.isSearching,
            };
        case actionTypes.PIPELINE_SEARCH_CLASSES:
            return {
                ...state,
                isClassesSearching: action.isSearching,
            };
        case actionTypes.PIPELINE_SEARCH_K_MATRIX:
            return {
                ...state,
                isKMatrixSearching: action.isSearching,
            };
        case actionTypes.PIPELINE_DEAL_SORTING_CHANGED:
            return {
                ...state,
                dealSortBy: action.sortBy,
                transactions: [],
                transactionsOffset: 0,
                numberOfHiddenTransactions: undefined,
                dealSortDirection: action.direction,
            };
        case actionTypes.PIPELINE_CLASS_SORTING_CHANGED:
            return {
                ...state,
                classSortBy: action.sortBy,
                classes: [],
                classesOffset: 0,
                numberOfHiddenClasses: undefined,
                classSortDirection: action.direction,
            };
        case actionTypes.PIPELINE_K_MATRIX_SORTING_CHANGED:
            return {
                ...state,
                kMatrixSortBy: action.sortBy,
                kMatrix: [],
                kMatrixOffset: 0,
                totalKMatrixClassItemsCount: 0,
                kMatrixSortDirection: action.direction,
                numberOfHiddenKMatrix: undefined,
            };
        case actionTypes.PIPELINE_STORE_TRANSACTIONS: {
            const transactions =
                pipelineType === PipelineType.IOIs
                    ? action.transactions.items.map((t: OriginatingTransaction) => ({
                        ...t,
                        classes: withIndicatingRefinancedIoIs(t.classes),
                    }))
                    : action.transactions.items;

            return {
                ...state,
                transactions: state.transactions
                    ? [...state.transactions, ...transactions]
                    : transactions,
                numberOfHiddenTransactions: action.transactions.numberOfHiddenItems,
                transactionsOffset:
                    state.transactionsOffset + transactions.length,
                hasMoreTransactions:
                    transactions.length ===
                    constants.defaultTransactionsSearchCriteria.count,
            };
        }
        case actionTypes.PIPELINE_STORE_CLASSES: {
            const classes =
                pipelineType === PipelineType.IOIs ? withIndicatingRefinancedIoIs(action.classes.items) : action.classes.items;

            return {
                ...state,
                classes: state.classes
                    ? [...state.classes, ...classes]
                    : classes,
                numberOfHiddenClasses: action.classes.numberOfHiddenItems,
                classesOffset: state.classesOffset + classes.length,
                hasMoreTransactionsClasses:
                    classes.length ===
                    constants.defaultTransactionsSearchCriteria.count,
            };
        }
        case actionTypes.PIPELINE_STORE_K_MATRIX: {
            return {
                ...state,
                kMatrix: state.kMatrix ? [...state.kMatrix, ...action.kMatrixList.items] : action.kMatrixList.items,
                kMatrixOffset: state.kMatrixOffset + action.kMatrixList.items.length,
                hasMoreKMatrix: action.kMatrixList.items.length === constants.defaultTransactionsSearchCriteria.count,
                numberOfHiddenKMatrix: action.kMatrixList.numberOfHiddenItems,
                totalKMatrixClassItemsCount: action.kMatrixList.totalClassItemsCount,
                isKMatrixSearching: action.isSearching,
            };
        }
        case actionTypes.PIPELINE_SUMMARY_REQUESTING_STATE:
            return {
                ...state,
                summaryRequestState: action.requestState
            };
        case actionTypes.PIPELINE_STORE_SUMMARY:
            return {
                ...state,
                transactionsStatistic: action.transactionsSummary,
                classesStatistic: action.classesSummary,
                summaryRequestState: action.summaryRequestState
            }
        case actionTypes.PIPELINE_RESET_TRANSACTIONS:
            return {
                ...state,
                classesOffset: 0,
                transactionsOffset: 0,
                kMatrixOffset: 0,
                totalKMatrixClassItemsCount: 0,
                transactionsStatistic: undefined,
                classesStatistic: undefined,
                transactions: [],
                classes: [],
                kMatrix: [],
                numberOfHiddenTransactions: undefined,
                numberOfHiddenClasses: undefined,
                numberOfHiddenKMatrix: undefined,
            };
        case actionTypes.PIPELINE_SET_EXPANDED:
            return {
                ...state,
                isExpanded: action.expanded,
            };
        case actionTypes.PIPELINE_RESET:
            return initialState;
        case actionTypes.PIPELINE_EXPORT_LOAD:
            return {
                ...state,
                isExportLoad: action.isLoading,
            };
        case actionTypes.PIPELINE_SET_SYNDICATE_CONTACTS:
            return {
                ...state,
                syndicateContacts: action.contacts ? {
                    arrangerName: action.arrangerName,
                    contacts: sortSyndicateContacts(action.contacts) ?? [],
                } : null,
            };
        case actionTypes.PIPELINE_SET_ANY_IOIS_EXISTS:
            return {
                ...state,
                anyIOIsExists: action.anyIOIsExists,
            };
        case actionTypes.PIPELINE_TRANSACTION_NOTE_ADDED:
            return {
                ...state,
                transactions: state.transactions?.map(t =>
                    t.dealReferenceName === action.dealReferenceName && t.referenceName === action.transactionReferenceName
                        ? { ...t, notesCount: (t.notesCount ?? 0) + 1 }
                        : t),
            }
        case actionTypes.PIPELINE_TRANSACTION_NOTE_DELETED:
            return {
                ...state,
                transactions: state.transactions?.map(t =>
                    t.dealReferenceName === action.dealReferenceName && t.referenceName === action.transactionReferenceName
                        ? { ...t, notesCount: Math.max((t.notesCount ?? 0) - action.count, 0) }
                        : t),
            }
        case actionTypes.PIPELINE_K_MATRIX_VIEW_TYPE_CHANGED:
            return {
                ...state,
                kMatrixViewType: action.viewType,
                kMatrixViewTypeAutoChange: action.autoChange
            }
        default:
            return state;
    }
};
